// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// API
import { deleteExam } from "../api";

export const useDeleteExams = (
  clinicId: string | undefined,
  patientId: string
) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (examIds: Array<string>) => {
      if (!clinicId || !patientId) throw new Error();

      try {
        const token = await getToken();
        return deleteExam(token, clinicId, patientId, examIds);
      } catch (error) {
        throw error;
      }
    },
    // Invalidate exams query cache
    onSuccess: (_, examIds) => {
      queryClient.invalidateQueries({ queryKey: ["exams"] });

      queryClient.invalidateQueries({ queryKey: ["patients"] });
      setSnackbarInfo({
        variant: "success",
        message: t("Exam deleted successfully!", { count: examIds.length }),
      });
    },
    onError: (_, examIds) => {
      setSnackbarInfo({
        variant: "error",
        message: t("Exam could not be deleted!", { count: examIds.length }),
      });
    },
  });
};
